import { Modal, Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import { useEffect, useRef, useState } from "react";
import reportlogo from "assets/reportlogo.svg";
import dayjs from "dayjs";
import { currencySymbolMap } from "common/Constants";
import { CALL_FEATCH_API } from "common/API";

const fmt = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const ViewModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-modal-header {
    border-bottom: none;
    padding: 24px 24px 8px;
    border-radius: 12px 12px 0 0;
  }
  .ant-modal-title {
    font-weight: 700;
    font-size: 1.15rem;
    color: #1F243B;
  }
  .ant-modal-body {
    padding: 30px 40px 24px;
  }
  .ant-modal-footer {
    display: none;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: nowrap;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: #f3f4f6;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 12.5px;
  color: #444;
  gap: 5px;
  white-space: nowrap;
  line-height: 1.5;
`;

const ApprovedBadge = styled(MetaBadge)`
  background: #e6f4f0;
  color: #1a7a5e;
`;

const BadgeLabel = styled.span`
  background: #EBEBEB;
  color: #636980;
  font-weight: 500;
  font-size: 14px;
`;

const BadgeValue = styled.span`
  font-weight: 400;
  color: #1F243B;
  font-size: 14px;
`;

const FundRequestIdText = styled.span`
  color: #636980;
  font-weight: 700;
  font-size: 18px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  row-gap: 14px;
  column-gap: 12px;
`;

const FieldLabel = styled.span`
  color: #636980;
  font-size: 14px;  
  padding-top: 1px;
`;

const FieldValue = styled.span`
  color: #1F243B;
  font-size: 16px;
  font-weight: 450;
`;

const DescText = styled.span`
  color: #333;
  font-size: 13.5px;
  line-height: 1.65;
  grid-column: 1 / -1;
  margin-top: 5px;
`;

const DescriptionWrapper = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1f243b;
`;

const InvoiceWrapper = styled.div`
  font-family: Arial, sans-serif;
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 24px;
`;

const FirmLogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const LogoBox = styled.div`
  width: 60px;
  height: 60px;
  border: 1px solid #ddd;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const FirmName = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1A227F;
`;

const FirmSub = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #636980;
  text-transform: uppercase;
  margin-top: 2px;
`;

const InvMeta = styled.div`
  text-align: right;
  font-size: 14px;
  line-height: 1.8;
  span.label { font-weight: 700; color: #1A237E; }
  span.value { font-weight: 400; color: #374151; }
`;

const ClientSection = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 28px;
`;

const ClientBlock = styled.div`
  flex: 1;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #636980;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const ClientName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1F243B;
  margin-bottom: 4px;
`;

const ClientAddr = styled.div`
  font-size: 14px;
  color: #1F243B;
  line-height: 1.6;
`;

const ContactName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
`;

const DescBox = styled.div`
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;
`;

const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
`;

const StaffBlock = styled.div`
  small { font-size: 14px; color: #636980; display: block; font-weight: 400; }
  p { font-size: 14px; font-weight: 600; color: #1F243B; margin-top: 2px; }
`;

const FeeTable = styled.table`
  width: 100%;
  font-size: 14px;
  border-collapse: collapse;
  td { padding: 5px 0; color: #1F243B; font-weight: 400; }
  td:last-child { text-align: right; color: #1F243B; font-weight: 500; }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #E5E7EB;
  padding-top: 10px;
  margin-top: 8px;
`;

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1F243B;
`;

const TotalVal = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #1A237E;
`;

const Divider = styled.hr`
  border: 1px solid #e5e7eb;
  color: #E5E7EB;
`;

const CertText = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #636980;
  margin-bottom: 30px;
  margin-top: 30px;
  line-height: 1.7;
`;

const ApprovedRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 14px;
  margin-bottom: 20px;
`;

const PrintBtn = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 auto;
  background: #C2FAE5;
  border-color: #C2FAE5;
  color: #1F243B;
  border-radius: 4px;
  font-weight: 500;
  height: 40px;
  padding: 0px 24px;
  &:hover, &:focus {
    background: #C2FAE5 !important;
    border-color: #C2FAE5 !important;
    color: #1F243B !important;
  }
`;

const formatDate = (dateStr) => {
  if (!dateStr) return "---";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = d.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? "st" :
      day % 10 === 2 && day !== 12 ? "nd" :
        day % 10 === 3 && day !== 13 ? "rd" : "th";
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day}${suffix} ${month} ${year}`;
};

const formatCreatedAt = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = d.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? "st" :
      day % 10 === 2 && day !== 12 ? "nd" :
        day % 10 === 3 && day !== 13 ? "rd" : "th";
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day}${suffix} ${month}, ${year}`;
};

const val = (v) => (v && v !== "MID0" ? v : "---");

const buildClientAddress = (clientRes = {}) => {
  return [
    clientRes.registeredaddress,
    clientRes.city,
    clientRes.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const FundAddedView = ({ record }) => (
  <FieldGrid>
    <FieldLabel>Client</FieldLabel>
    <FieldValue>{val(record.clientname)}</FieldValue>

    <FieldLabel>Matter</FieldLabel>
    <FieldValue>
      {record.matterid && record.matterid !== "MID0" ? val(record.mattername) : "---"}
    </FieldValue>

    <FieldLabel>Date of Transaction</FieldLabel>
    <FieldValue>{formatDate(record.dateoftransaction)}</FieldValue>

    <FieldLabel>Amount</FieldLabel>
    <FieldValue>
      {Number(record.fundin) > 0
        ? `${currencySymbolMap[record.currency]}${fmt(record.fundin)}`
        : Number(record.fundout) > 0
          ? `${currencySymbolMap[record.currency]}${fmt(record.fundout)}`
          : "---"}
    </FieldValue>

    <FieldLabel>Source</FieldLabel>
    <FieldValue>{val(record.sourcename)}</FieldValue>

    <FieldLabel>Destination</FieldLabel>
    <FieldValue>{val(record.destinationname)}</FieldValue>

    <FieldLabel>Payment Method</FieldLabel>
    <FieldValue>{val(record.paymentmethod)}</FieldValue>

    <FieldLabel>Other Remarks</FieldLabel>
    <FieldValue>{val(record.otherremarks)}</FieldValue>

    {record.description ? (
      <DescriptionWrapper>
        <FieldLabel>Description</FieldLabel>
        <DescText>{val(record.description)}</DescText>
      </DescriptionWrapper>
    ) : (
      <>
        <FieldLabel>Description</FieldLabel>
        <FieldValue>---</FieldValue>
      </>
    )}
  </FieldGrid>
);

const FundMovedView = ({ record }) => (
  <FieldGrid>
    <FieldLabel>Client</FieldLabel>
    <FieldValue>{val(record.clientname)}</FieldValue>

    <FieldLabel>Date of Movement</FieldLabel>
    <FieldValue>{formatDate(record.createdat || record.dateofmovement)}</FieldValue>

    <FieldLabel>Type of Movement</FieldLabel>
    <FieldValue>{val(record.typeofmovement)}</FieldValue>

    <FieldLabel>Source</FieldLabel>
    <FieldValue>{val(record.sourcename)}</FieldValue>

    <FieldLabel>Destination</FieldLabel>
    <FieldValue>{val(record.destinationname)}</FieldValue>

    <FieldLabel>Amount Transferred</FieldLabel>
    <FieldValue>
      {Number(record.fundin) > 0
        ? `${currencySymbolMap[record.currency]}${fmt(record.fundin)}`
        : Number(record.fundout) > 0
          ? `${currencySymbolMap[record.currency]}${fmt(record.fundout)}`
          : "---"}
    </FieldValue>

    <FieldLabel>Description</FieldLabel>
    {record.description ? (
      <DescText>{val(record.description)}</DescText>
    ) : (
      <FieldValue>---</FieldValue>
    )}
  </FieldGrid>
);

const FundRequestView = ({ record }) => (
  <FieldGrid>
    <FieldLabel>Client</FieldLabel>
    <FieldValue>{val(record.clientname)}</FieldValue>

    <FieldLabel>Requesting Fund for</FieldLabel>
    <FieldValue>{val(record.fundrequesttype || "---")}</FieldValue>

    {record.fundrequesttype === "Matter Fund" ? (
      <>
        <FieldLabel>Matter Name</FieldLabel>
        <FieldValue>{val(record.mattername)}</FieldValue>
      </>
    ) : null}

    <FieldLabel>Amount</FieldLabel>
    <FieldValue>
      {`${currencySymbolMap[record.currency]}${fmt(record.fundrequestamount)}` || "---"}
    </FieldValue>

    <FieldLabel>Due Date</FieldLabel>
    <FieldValue>{formatDate(record.duedate || "---")}</FieldValue>

    <FieldLabel>Description</FieldLabel>
    {record.description ? (
      <DescText>{val(record.description)}</DescText>
    ) : (
      <FieldValue>---</FieldValue>
    )}

  </FieldGrid>
);

const BillingView = ({ record, invoiceRef }) => {
  const userList = useSelector(getUserList);

  const billingData = record?.billingdata || {};
  const clientRes = record?.clientRes || {};

  const leadAttorneyUser = userList?.find(
    (item) => item?.userid === billingData?.leadattorney
  );
  const referringAttorneyUser = userList?.find(
    (item) => item?.userid === billingData?.referringattorney
  );

  const approvedByUser = userList?.find((item) => item?.userid === billingData.billingapprovedby);

  const leadAttorneyName = leadAttorneyUser?.fullname || billingData?.leadattorney || "---";
  const referringAttorneyName = referringAttorneyUser?.fullname || billingData?.referringattorney || "---";
  const approvedByName = approvedByUser?.fullname || billingData.billingapprovedby || "---";

  let currencysymbol = currencySymbolMap[billingData?.currency?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")];

  const taxPercentage = Number(billingData?.tax) || 0;

  const fees = Number(billingData?.billingfee || 0);

  const billingDisbursement = (billingData?.claims || []).reduce((sum, cl) => {
    if ((cl?.type || "").toLowerCase() === "subcontract") return sum;
    return sum + parseFloat(cl?.amount || 0);
  }, 0);

  const billingSubcontract = (billingData?.claims || []).reduce((sum, cl) => {
    if ((cl?.type || "").toLowerCase() !== "subcontract") return sum;
    return sum + parseFloat(cl?.amount || 0);
  }, 0);

  const vatdisbursement = (billingData?.claims || []).reduce((sum, cl) => {
    if ((cl?.type || "").toLowerCase() === "subcontract") return sum;
    if (cl?.taxtype !== "VAT") return sum;
    const amount = parseFloat(cl?.amount || 0);
    return sum + (amount * taxPercentage) / 100;
  }, 0);

  const vatsubcontract = (billingData?.claims || []).reduce((sum, cl) => {
    if ((cl?.type || "").toLowerCase() !== "subcontract") return sum;
    if (cl?.taxtype !== "VAT") return sum;
    const amount = parseFloat(cl?.amount || 0);
    return sum + (amount * taxPercentage) / 100;
  }, 0);

  const vatRate = billingData?.vat && billingData.vat.trim().toUpperCase() === "VAT" ? taxPercentage : 0;
  const taxAmount = ((fees * vatRate) / 100) + vatdisbursement + vatsubcontract;
  const totalAmount = fees + billingDisbursement + billingSubcontract + taxAmount;

  const firmName = billingData?.mattername || "Synergy Pvt Ltd";
  const firmLocation = "SYDNEY, AUSTRALIA";
  const billingDate = billingData?.billingdate
    ? dayjs(billingData.billingdate).format("DD-MM-YYYY")
    : "";
  const matterid = billingData?.matterid || "---";
  const billingid = billingData?.billingid || "---";
  const clientName = billingData?.clientname || clientRes?.clientname || "---";
  const clientAddress = buildClientAddress(clientRes);
  const contactPerson = billingData?.contactperson || "---";
  const currency = billingData?.currency?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "---";
  const description = billingData?.description || "---";
  const amount = Number(billingData?.billingfee).toFixed(2) ?? 0;
  const disbursement = Number(billingDisbursement).toFixed(2) ?? 0;
  const subcontract = Number(billingSubcontract).toFixed(2) ?? 0;
  const vatAmount = Number(taxAmount).toFixed(2) ?? 0;
  const total = Number(totalAmount).toFixed(2) ?? 0;
  const approvedBy = approvedByName || "---";
  const approvedDate = dayjs(billingData?.billingapproveddate).format("DD-MM-YYYY") || "---";

  return (
    <InvoiceWrapper ref={invoiceRef}>
      <InvoiceHeader>
        <FirmLogoRow>
          <LogoBox>
            {reportlogo ? <img src={reportlogo} alt="logo" /> : (
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M14 4L4 9v10l10 5 10-5V9L14 4z" stroke="#1A227F" strokeWidth="1.5" fill="none" />
                <path d="M14 4v16M4 9l10 5 10-5" stroke="#1A227F" strokeWidth="1.5" />
              </svg>
            )}
          </LogoBox>
          <div>
            <FirmName>{firmName}</FirmName>
            <FirmSub>{firmLocation}</FirmSub>
          </div>
        </FirmLogoRow>
        <InvMeta>
          <span className="label">Date:</span> <span className="value">{billingDate}</span><br />
          <span className="label">Matter ID:</span> <span className="value">{matterid}</span><br />
          <span className="label">Billing ID:</span> <span className="value">{billingid}</span>
        </InvMeta>
      </InvoiceHeader>

      <ClientSection>
        <ClientBlock>
          <SectionLabel>Client Information</SectionLabel>
          <ClientName>{clientName}</ClientName>
          {clientAddress && (
            <ClientAddr>
              {clientAddress.split("\n").map((line, i) => (
                <span key={i}>{line}<br /></span>
              ))}
            </ClientAddr>
          )}
        </ClientBlock>
        <ClientBlock style={{ textAlign: "right" }}>
          <SectionLabel>Contact Person</SectionLabel>
          <ContactName>{contactPerson}</ContactName>
          <ClientAddr style={{ marginTop: 4 }}>
            Currency: <b style={{ color: "#1F243B" }}>{currency}</b>
          </ClientAddr>
        </ClientBlock>
      </ClientSection>

      <DescBox>
        <SectionLabel>Description of Services Rendered</SectionLabel>
        <div style={{ fontSize: 14, color: "#1F243B", fontWeight: 400 }}>{description}</div>
      </DescBox>

      <BottomGrid>
        <div>
          <SectionLabel>Staff Assignment</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <StaffBlock>
              <small>Lead Attorney</small>
              <p>{leadAttorneyName}</p>
            </StaffBlock>
            <StaffBlock>
              <small>Referring Attorney</small>
              <p>{referringAttorneyName}</p>
            </StaffBlock>
          </div>
        </div>
        <div>
          <FeeTable>
            <tbody>
              <tr><td>Fees</td><td>{`${currencysymbol}${fmt(amount)}`}</td></tr>
              <tr><td>Disbursement (Claims)</td><td>{`${currencysymbol}${fmt(disbursement)}`}</td></tr>
              <tr><td>Subcontract (Claims)</td><td>{`${currencysymbol}${fmt(subcontract)}`}</td></tr>
              <tr><td>VAT – {billingData?.tax}%</td><td>{`${currencysymbol}${fmt(vatAmount)}`}</td></tr>
            </tbody>
          </FeeTable>
          <TotalRow>
            <TotalLabel>Total ({currency})</TotalLabel>
            <TotalVal>{currencysymbol}{fmt(total)}</TotalVal>
          </TotalRow>
        </div>
      </BottomGrid>

      <Divider />

      <CertText>
        I, certify that the above amount billed is in accordance with the matter and/or the assignment has been completed at client satisfaction.
      </CertText>

      <ApprovedRow>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.07em", textTransform: "uppercase", color: "#636980", fontWeight: 700 }}>Approved By</div>
          <div style={{ fontSize: 16, color: "#1F243B", fontWeight: 500 }}>{approvedBy}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.07em", textTransform: "uppercase", color: "#636980", fontWeight: 700 }}>Signature/Date</div>
          <div style={{ fontSize: 16, color: "#1F243B", fontWeight: 500 }}>{approvedDate}</div>
        </div>
      </ApprovedRow>
    </InvoiceWrapper>
  );
};

export const getCategoryType = (category = "") => {
  const lower = category.toLowerCase();
  if (lower.includes("fund added") || lower === "fund added") return "fund_added";
  if (lower.includes("fund moved") || lower === "fund moved") return "fund_moved";
  if (lower.includes("fund request") || lower.startsWith("fr")) return "fund_request";
  if (lower.includes("billing") || lower.startsWith("bi")) return "billing";
  return null;
};

const TransactionViewModal = ({ open, onClose, record }) => {
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);

  const invoiceRef = useRef(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [fetchedBillingData, setFetchedBillingData] = useState(null);
  const [fetchedClientRes, setFetchedClientRes] = useState(null);
  const raw = record?._raw || record;
  
  useEffect(() => {
    if (!open || !isBilling) {
      setFetchedBillingData(null);
      setFetchedClientRes(null);
      return;
    }

    const billingId = raw?.billingid;
    const clientId = raw?.clientid;
    if (!billingId || !clientId) return;

    const fetchData = async () => {
      setBillingLoading(true);
      try {
        const res = await CALL_FEATCH_API(
          `insyts/billing?billingid=${billingId}&clientid=${clientId}`,
          "GET"
        );
        // response.data is an array — take first item as billingdata
        setFetchedBillingData(res?.response?.data?.[0] || {});
        setFetchedClientRes(res?.response?.clientRes || {});
      } catch (err) {
        console.error("Failed to fetch billing data:", err);
        setFetchedBillingData({});
        setFetchedClientRes({});
      } finally {
        setBillingLoading(false);
      }
    };

    fetchData();
  }, [open, raw?.billingid, raw?.clientid]);

  if (!record) return null;
  const resolvedCatType = getCategoryType(raw?.category || "");
  const isBilling = resolvedCatType === "billing";

  const effectiveRecord = isBilling
    ? { ...raw, billingdata: fetchedBillingData || {}, clientRes: fetchedClientRes || {} }
    : raw;
  const createdByUser = userList?.find(
    (item) => item?.userid === effectiveRecord.fundrequestcreatedby
  );
  const approvedByUser = userList?.find(
    (item) => item?.userid === effectiveRecord.fundrequestapprovedby
  );

  const createdByName = userInfo?.userid === effectiveRecord.fundrequestcreatedby ? "You" : createdByUser?.fullname || effectiveRecord.fundrequestcreatedby || "---";
  const approvedByName = userInfo?.userid === effectiveRecord.fundrequestapprovedby ? "You" : approvedByUser?.fullname || effectiveRecord.fundrequestapprovedby || "---";

  const handlePrint = () => {
    const bd = effectiveRecord?.billingdata || {};
    const clientRes = effectiveRecord?.clientRes || {};

    let currencysymbol = currencySymbolMap[bd?.currency?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")];

    const taxPercentage = Number(bd?.tax) || 0;

    const fees = Number(bd?.billingfee || 0);

    const billingDisbursement = (bd?.claims || []).reduce((sum, cl) => {
      if ((cl?.type || "").toLowerCase() === "subcontract") return sum;
      return sum + parseFloat(cl?.amount || 0);
    }, 0);

    const billingSubcontract = (bd?.claims || []).reduce((sum, cl) => {
      if ((cl?.type || "").toLowerCase() !== "subcontract") return sum;
      return sum + parseFloat(cl?.amount || 0);
    }, 0);

    const vatdisbursement = (bd?.claims || []).reduce((sum, cl) => {
      if ((cl?.type || "").toLowerCase() === "subcontract") return sum;
      if (cl?.taxtype !== "VAT") return sum;
      const amount = parseFloat(cl?.amount || 0);
      return sum + (amount * taxPercentage) / 100;
    }, 0);

    const vatsubcontract = (bd?.claims || []).reduce((sum, cl) => {
      if ((cl?.type || "").toLowerCase() !== "subcontract") return sum;
      if (cl?.taxtype !== "VAT") return sum;
      const amount = parseFloat(cl?.amount || 0);
      return sum + (amount * taxPercentage) / 100;
    }, 0);

    const vatRate = bd?.vat && bd.vat.trim().toUpperCase() === "VAT" ? taxPercentage : 0;
    const taxAmount = ((fees * vatRate) / 100) + vatdisbursement + vatsubcontract;
    const totalAmount = fees + billingDisbursement + billingSubcontract + taxAmount;

    const firmName = bd?.mattername || "Synergy Pvt Ltd";
    const firmLocation = "SYDNEY, AUSTRALIA";
    const billingDate = bd?.billingdate ? dayjs(bd.billingdate).format("DD-MM-YYYY") : "---";
    const matterid = bd.matterid || "---";
    const billingid = bd.billingid || "---";
    const clientName = bd.clientname || clientRes?.clientname || "---";
    const clientAddress = buildClientAddress(clientRes);
    const contactPerson = bd.contactperson || "---";
    const currency = bd.currency?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "US Dollar";
    const description = bd.description || effectiveRecord.description || "---";

    const leadAttorneyUser = userList?.find((u) => u?.userid === bd.leadattorney);
    const referringAttorneyUser = userList?.find((u) => u?.userid === bd.referringattorney);
    const approvedByUser = userList?.find((u) => u?.userid === bd.billingapprovedby);

    const leadAttorneyName = leadAttorneyUser?.fullname || bd.leadattorney || "---";
    const referringAttorneyName = referringAttorneyUser?.fullname || bd.referringattorney || "---";
    const approvedByName = approvedByUser?.fullname || bd.billingapprovedby || "---";

    const amount = Number(bd?.billingfee).toFixed(2) ?? 0;
    const disbursement = Number(billingDisbursement).toFixed(2) ?? 0;
    const subcontract = Number(billingSubcontract).toFixed(2) ?? 0;
    const vatAmount = Number(taxAmount).toFixed(2) ?? 0;
    const vatPct = taxPercentage;
    const total = Number(totalAmount).toFixed(2) ?? 0;
    const approvedBy = approvedByName || "---";
    const approvedDate = dayjs(bd.billingapproveddate).format("DD-MM-YYYY") || "---";

    const fmtNum = (n) =>
      Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const clientAddrHTML = clientAddress
      ? clientAddress.split("\n").map((l) => `<span>${l}<br/></span>`).join("")
      : "";

    const win = window.open("", "_blank", "width=860,height=1000");
    if (!win) return;

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Invoice - ${billingid}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;padding:40px 44px;color:#1F243B;font-size:14px;background:#fff}

    .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:1px solid #e0e0e0;margin-bottom:24px}
    .logo-row{display:flex;align-items:center;gap:14px}
    .logo-box{width:60px;height:60px;border:1px solid #ddd;border-radius:6px;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0}
    .logo-box img{width:100%;height:100%;object-fit:contain}
    .firm-name{font-size:24px;font-weight:700;color:#1A227F}
    .firm-sub{font-size:14px;font-weight:600;color:#636980;text-transform:uppercase;margin-top:2px}
    .inv-meta{text-align:right;font-size:14px;line-height:1.8}
    .inv-meta .label{font-weight:700;color:#1A237E}
    .inv-meta .value{font-weight:400;color:#374151}

    .client-section{display:flex;gap:40px;margin-bottom:28px}
    .client-block{flex:1}
    .client-block.right{text-align:right}
    .section-label{font-size:12px;font-weight:700;letter-spacing:1px;color:#636980;text-transform:uppercase;margin-bottom:12px}
    .client-name{font-size:18px;font-weight:700;color:#1F243B;margin-bottom:4px}
    .client-addr{font-size:14px;color:#1F243B;line-height:1.6}
    .contact-name{font-size:14px;font-weight:600;color:#333;margin-bottom:2px}

    .desc-box{border:1px solid #E5E7EB;border-radius:8px;padding:14px 16px;margin-bottom:20px}
    .desc-text{font-size:14px;color:#1F243B;font-weight:400;margin-top:6px}

    .bottom-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}
    .staff-inner{display:grid;grid-template-columns:1fr 1fr;gap:8px}
    .staff-block small{font-size:14px;color:#636980;display:block;font-weight:400}
    .staff-block p{font-size:14px;font-weight:600;color:#1F243B;margin-top:2px}
    .fee-table{width:100%;font-size:14px;border-collapse:collapse}
    .fee-table td{padding:5px 0;color:#1F243B;font-weight:400}
    .fee-table td:last-child{text-align:right;color:#1F243B;font-weight:500}
    .total-row{display:flex;justify-content:space-between;align-items:center;border-top:1px solid #E5E7EB;padding-top:10px;margin-top:8px}
    .total-label{font-size:16px;font-weight:700;color:#1F243B}
    .total-val{font-size:20px;font-weight:700;color:#1A237E}

    .divider{border:none;border-top:1px solid #e5e7eb;margin:0}

    .cert-text{font-size:12px;font-weight:400;color:#636980;margin-top:30px;margin-bottom:30px;line-height:1.7}
    .approved-row{display:flex;justify-content:space-between;align-items:center;padding-top:14px}
    .approved-group{display:flex;align-items:center;gap:8px}
    .approved-label{font-size:12px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:#636980}
    .approved-val{font-size:16px;font-weight:500;color:#1F243B}

    @media print{body{padding:20px 24px}}
  </style>
</head>
<body>

  <div class="header">
    <div class="logo-row">
      <div class="logo-box">
        <svg width="32" height="32" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 4L4 9v10l10 5 10-5V9L14 4z" stroke="#1A227F" stroke-width="1.5" fill="none"/>
          <path d="M14 4v16M4 9l10 5 10-5" stroke="#1A227F" stroke-width="1.5"/>
        </svg>
      </div>
      <div>
        <div class="firm-name">${firmName}</div>
        <div class="firm-sub">${firmLocation}</div>
      </div>
    </div>
    <div class="inv-meta">
      <span class="label">Date:</span> <span class="value">${billingDate}</span><br/>
      <span class="label">Matter ID:</span> <span class="value">${matterid}</span><br/>
      <span class="label">Billing ID:</span> <span class="value">${billingid}</span>
    </div>
  </div>

  <div class="client-section">
    <div class="client-block">
      <div class="section-label">Client Information</div>
      <div class="client-name">${clientName}</div>
      ${clientAddrHTML ? `<div class="client-addr">${clientAddrHTML}</div>` : ""}
    </div>
    <div class="client-block right">
      <div class="section-label">Contact Person</div>
      <div class="contact-name">${contactPerson}</div>
      <div class="client-addr" style="margin-top:4px">
        Currency: <b style="color:#1F243B">${currency}</b>
      </div>
    </div>
  </div>

  <div class="desc-box">
    <div class="section-label">Description of Services Rendered</div>
    <div class="desc-text">${description}</div>
  </div>

  <div class="bottom-grid">
    <div>
      <div class="section-label">Staff Assignment</div>
      <div class="staff-inner">
        <div class="staff-block">
          <small>Lead Attorney</small>
          <p>${leadAttorneyName}</p>
        </div>
        <div class="staff-block">
          <small>Referring Attorney</small>
          <p>${referringAttorneyName}</p>
        </div>
      </div>
    </div>
    <div>
      <table class="fee-table">
        <tbody>
          <tr><td>Fees</td><td>${currencysymbol}${fmtNum(amount)}</td></tr>
          <tr><td>Disbursement (Claims)</td><td>${currencysymbol}${fmtNum(disbursement)}</td></tr>
          <tr><td>Subcontract (Claims)</td><td>${currencysymbol}${fmtNum(subcontract)}</td></tr>
          <tr><td>VAT – ${vatPct}%</td><td>${currencysymbol}${fmtNum(vatAmount)}</td></tr>
        </tbody>
      </table>
      <div class="total-row">
        <span class="total-label">Total (${currency})</span>
        <span class="total-val">${currencysymbol}${fmtNum(total)}</span>
      </div>
    </div>
  </div>

  <hr class="divider"/>

  <p class="cert-text">
    I, certify that the above amount billed is in accordance with the matter and/or the assignment has been completed at client satisfaction.
  </p>

  <div class="approved-row">
    <div class="approved-group">
      <div class="approved-label">Approved By</div>
      <div class="approved-val">${approvedBy}</div>
    </div>
    <div class="approved-group">
      <div class="approved-label">Signature/Date</div>
      <div class="approved-val">${approvedDate}</div>
    </div>
  </div>

</body>
</html>`);

    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 400);
  };

  let title = effectiveRecord.category || "Transaction Detail";
  if (resolvedCatType === "fund_added") title = "Fund Added";
  if (resolvedCatType === "fund_moved") title = "Fund Moved";
  if (resolvedCatType === "fund_request") {
    title = (
      <>
        Fund Request{" "}
        <FundRequestIdText>({effectiveRecord.fundrequestid})</FundRequestIdText>
      </>
    );
  }
  if (resolvedCatType === "billing") title = "";

  const showMeta = resolvedCatType === "fund_request";
  // const isBilling = resolvedCatType === "billing";

  return (
    <ViewModal
      open={open}
      onCancel={onClose}
      width={isBilling ? 700 : 650}
      destroyOnClose
      title={
        <TitleRow>
          <span>{title}</span>
        </TitleRow>
      }
    >
      {showMeta && (
        <MetaRow>
          <MetaBadge>
            <BadgeLabel>Created by:</BadgeLabel>
            <BadgeValue>
              {createdByName}; {formatCreatedAt(effectiveRecord.fundrequestcreatedat)}
            </BadgeValue>
          </MetaBadge>

          {effectiveRecord.fundrequestapprovedby && (
            <ApprovedBadge>
              <BadgeLabel style={{ color: "#03C83E" }}>Approved by</BadgeLabel>
              <BadgeValue>
                {approvedByName}; {formatCreatedAt(effectiveRecord.fundrequestapprovedat)}
              </BadgeValue>
            </ApprovedBadge>
          )}
        </MetaRow>
      )}

      {resolvedCatType === "fund_added" && <FundAddedView record={effectiveRecord} />}
      {resolvedCatType === "fund_moved" && <FundMovedView record={effectiveRecord} />}
      {resolvedCatType === "fund_request" && <FundRequestView record={effectiveRecord} />}

      {isBilling && (
        <>
          <BillingView record={effectiveRecord} invoiceRef={invoiceRef} />
          <div style={{ textAlign: "center", marginTop: 20, paddingTop: 30, paddingBottom: 30 }}>
            <PrintBtn icon={<PrinterOutlined />} onClick={handlePrint}>
              Print Invoice
            </PrintBtn>
          </div>
        </>
      )}

      {!["fund_added", "fund_moved", "fund_request", "billing"].includes(resolvedCatType) && (
        <FieldGrid>
          <FieldLabel>Client</FieldLabel>
          <FieldValue>{val(effectiveRecord.clientname)}</FieldValue>

          <FieldLabel>Category</FieldLabel>
          <FieldValue>{val(effectiveRecord.category)}</FieldValue>

          <FieldLabel>Source</FieldLabel>
          <FieldValue>{val(effectiveRecord.sourcename)}</FieldValue>

          <FieldLabel>Destination</FieldLabel>
          <FieldValue>{val(effectiveRecord.destinationname)}</FieldValue>

          <FieldLabel>Fund Out</FieldLabel>
          <FieldValue>{effectiveRecord.fundout ? `${currencySymbolMap[effectiveRecord.currency]}${fmt(effectiveRecord.fundout)}` : "---"}</FieldValue>

          <FieldLabel>Fund In</FieldLabel>
          <FieldValue>{effectiveRecord.fundin ? `${currencySymbolMap[effectiveRecord.currency]}${fmt(effectiveRecord.fundin)}` : "---"}</FieldValue>

          {effectiveRecord.description && (
            <>
              <FieldLabel style={{ gridColumn: "1 / -1" }}>Description</FieldLabel>
              <DescText style={{ gridColumn: "1 / -1" }}>{effectiveRecord.description}</DescText>
            </>
          )}
        </FieldGrid>
      )}
    </ViewModal>
  );
};

export default TransactionViewModal;